import { useMemo } from 'react';
import { CUSTOMER_SUBSCRIPTION_ACTION_OPTIONS } from '../constants';
import {
    CUSTOMER_SUBSCRIPTION_INTERVAL_LABELS,
    CUSTOMER_SUBSCRIPTION_STATUS,
    CUSTOMER_SUBSCRIPTION_STATUS_LABELS,
    CUSTOMER_SUBSCRIPTION_STATUS_TAG_CLASS,
} from '@/lib/constants';
import type { CustomerProfile, CustomerSubscription } from '../types/customer';

export interface SubscriptionAction {
    id: string;
    label: string;
}

export interface SubscriptionViewModel extends CustomerSubscription {
    intervalLabel: string;
    statusLabel: string;
    actions: SubscriptionAction[];
    statusClassName?: string;
    validUntilLabel?: string | null;
}

export interface CustomerSubscriptionsResult {
    subscriptions: SubscriptionViewModel[];
    hasActiveSubscription: boolean;
    hasUpcomingAppointments: boolean;
}

/**
 * Transforms customer subscription data into view models with computed properties
 * @param customer - Customer profile with subscription data
 * @returns Formatted subscriptions with status indicators and available actions
 */
export function useCustomerSubscriptions(customer: CustomerProfile | null): CustomerSubscriptionsResult {
    return useMemo(() => {
        if (!customer) {
            return {
                subscriptions: [],
                hasActiveSubscription: false,
                hasUpcomingAppointments: false,
            };
        }

        const subscriptions: SubscriptionViewModel[] = (customer.subscriptions ?? []).map(subscription => {
            const intervalLabel =
                CUSTOMER_SUBSCRIPTION_INTERVAL_LABELS[subscription.billingInterval] ?? subscription.billingInterval;
            const statusLabel = CUSTOMER_SUBSCRIPTION_STATUS_LABELS[subscription.status] ?? 'Unknown';
            const actions = CUSTOMER_SUBSCRIPTION_ACTION_OPTIONS[subscription.status] ?? [];
            const validUntilLabel = subscription.validUntil ? `Valid until ${subscription.validUntil}` : null;

            return {
                ...subscription,
                intervalLabel,
                statusLabel,
                actions,
                statusClassName: CUSTOMER_SUBSCRIPTION_STATUS_TAG_CLASS[subscription.status] ?? '',
                validUntilLabel,
            };
        });

        const hasActiveSubscription = subscriptions.some(sub => sub.status === CUSTOMER_SUBSCRIPTION_STATUS.ACTIVE);
        const hasUpcomingAppointments = (customer.upcomingAppointments ?? []).length > 0;

        return {
            subscriptions,
            hasActiveSubscription,
            hasUpcomingAppointments,
        };
    }, [customer]);
}
